import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, User, Mail, Shield, Lock, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { AuthService } from '../Entities/AuthService';
import { useToast } from '@/Components/ui/use-toast';
import { validatePasswordStrength } from '../utils/passwordValidation';
import PasswordRequirements from './PasswordRequirements';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

export default function ProfileModal({ isOpen, onClose }) {
  useBodyScrollLock(isOpen);
  const { toast } = useToast();
  const [currentUser, setCurrentUser] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordValidation, setPasswordValidation] = useState({
    requirements: [],
    allMet: false
  });
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  useEffect(() => {
    if (isOpen) {
      const user = AuthService.getCurrentUser();
      setCurrentUser(user);
    } else {
      // Reset do formulário quando modal é fechado
      setShowPasswordForm(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordValidation({
        requirements: [],
        allMet: false
      });
      setPasswordsMatch(true);
    }
  }, [isOpen]);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    const updatedData = {
      ...passwordData,
      [name]: value
    };
    
    setPasswordData(updatedData);
    
    // Validar senha em tempo real
    if (name === 'newPassword') {
      const validation = validatePasswordStrength(value);
      setPasswordValidation(validation);
      
      // Verificar se coincide com confirmPassword
      if (updatedData.confirmPassword) {
        setPasswordsMatch(value === updatedData.confirmPassword);
      }
    }
    
    // Verificar coincidência quando digitar no campo de confirmação
    if (name === 'confirmPassword') {
      setPasswordsMatch(value === updatedData.newPassword);
    }
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();

    // Validações
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Preencha todos os campos'
      });
      return;
    }

    if (!passwordValidation.allMet) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'A nova senha não atende a todos os requisitos de segurança'
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'As senhas não coincidem'
      });
      return;
    }

    try {
      setIsChangingPassword(true);
      
      await AuthService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      toast({
        title: 'Sucesso',
        description: 'Senha alterada com sucesso!'
      });
      
      // Limpar e fechar
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordValidation({
        requirements: [],
        allMet: false
      });
      setPasswordsMatch(true);
      setShowPasswordForm(false);
      
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.error || 'Erro ao alterar senha. Verifique a senha atual.'
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Administrador';
      case 'admin': return 'Administrador';
      default: return 'Usuário';
    }
  };

  if (!isOpen || !currentUser) return null;

  // Portal: escapa do fluxo normal da página, evitando que containers com
  // margin/spacing (ex.: space-y-*) desloquem o overlay fixed.
  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#003098] to-[#0074f6] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Meu Perfil</h2>
              <p className="text-sm text-blue-100">Informações da conta</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Informações Pessoais */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <User className="w-5 h-5 text-[#0074f6]" />
              Informações Pessoais
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nome */}
              <div>
                <label className="text-sm font-medium text-slate-600">Nome Completo</label>
                <div className="mt-1 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-slate-900 font-medium">{currentUser.name}</p>
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-medium text-slate-600">E-mail</label>
                <div className="mt-1 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-slate-900 font-medium">{currentUser.email}</p>
                </div>
              </div>

              {/* Perfil */}
              <div>
                <label className="text-sm font-medium text-slate-600">Perfil de Acesso</label>
                <div className="mt-1 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    <Shield className="w-4 h-4 mr-1" />
                    {getRoleLabel(currentUser.role)}
                  </span>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="text-sm font-medium text-slate-600">Status da Conta</label>
                <div className="mt-1 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    Ativa
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Alterar Senha */}
          <div className="border-t border-slate-200 pt-6">
            {!showPasswordForm ? (
              <button
                onClick={() => setShowPasswordForm(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#0074f6] text-white rounded-lg hover:bg-[#003098] transition-colors"
              >
                <Lock className="w-5 h-5" />
                Alterar Senha
              </button>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-[#0074f6]" />
                    Alterar Senha
                  </h3>
                  <button
                    onClick={() => {
                      setShowPasswordForm(false);
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      setPasswordValidation({ requirements: [], allMet: false });
                      setPasswordsMatch(true);
                    }}
                    className="text-sm text-slate-600 hover:text-slate-900"
                  >
                    Cancelar
                  </button>
                </div>

                <form onSubmit={handleSubmitPassword} className="space-y-4">
                  {/* Senha Atual */}
                  <div>
                    <label className="text-sm font-medium text-slate-700">Senha Atual *</label>
                    <div className="mt-1 relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0074f6] focus:border-transparent"
                        placeholder="Digite sua senha atual"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Nova Senha */}
                  <div>
                    <label className="text-sm font-medium text-slate-700">Nova Senha *</label>
                    <div className="mt-1 relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0074f6] focus:border-transparent"
                        placeholder="Digite a nova senha (mínimo 8 caracteres)"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    
                    {/* Requisitos da senha em tempo real */}
                    {passwordData.newPassword && (
                      <PasswordRequirements 
                        requirements={passwordValidation.requirements}
                        allMet={passwordValidation.allMet}
                      />
                    )}
                  </div>

                  {/* Confirmar Senha */}
                  <div>
                    <label className="text-sm font-medium text-slate-700">Confirmar Nova Senha *</label>
                    <div className="mt-1 relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                          passwordData.confirmPassword && !passwordsMatch
                            ? 'border-red-300 focus:ring-red-500'
                            : passwordData.confirmPassword && passwordsMatch
                            ? 'border-green-300 focus:ring-green-500'
                            : 'border-slate-300 focus:ring-[#0074f6]'
                        }`}
                        placeholder="Confirme a nova senha"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    
                    {/* Feedback visual em tempo real */}
                    {passwordData.confirmPassword && (
                      <div className="mt-2">
                        {passwordsMatch ? (
                          <p className="text-sm text-green-600 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            As senhas coincidem
                          </p>
                        ) : (
                          <p className="text-sm text-red-600 flex items-center gap-1">
                            <XCircle className="w-4 h-4" />
                            As senhas não coincidem
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isChangingPassword}
                    className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {isChangingPassword ? 'Alterando...' : 'Confirmar Alteração'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
